/**
 * Shared Validation Utilities
 * Consolidated from frontend and backend validators
 * Source of truth for all validation and formatting logic
 */

/**
 * Validate CPF (Brazilian Individual Taxpayer Registry)
 * Implements full digit verification algorithm
 * @param cpf CPF string (can be with or without mask)
 * @returns true if CPF is valid, false otherwise
 */
export function validateCPF(cpf: string): boolean {
  // Remove mask characters
  const cleanCPF = cpf.replace(/[^\d]/g, '');
  
  // Check if has 11 digits
  if (cleanCPF.length !== 11) {
    return false;
  }
  
  // Check for known invalid CPFs (all digits the same)
  if (/^(\d)\1{10}$/.test(cleanCPF)) {
    return false;
  }
  
  // Validate first check digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
  }
  let checkDigit = 11 - (sum % 11);
  if (checkDigit >= 10) checkDigit = 0;
  if (checkDigit !== parseInt(cleanCPF.charAt(9))) {
    return false;
  }
  
  // Validate second check digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
  }
  checkDigit = 11 - (sum % 11);
  if (checkDigit >= 10) checkDigit = 0;
  if (checkDigit !== parseInt(cleanCPF.charAt(10))) {
    return false;
  }
  
  return true;
}

/**
 * Format CPF with mask (000.000.000-00)
 * Used by backend for final formatting
 * @param cpf CPF string (raw digits)
 * @returns Formatted CPF
 */
export function formatCPF(cpf: string): string {
  const cleanCPF = cpf.replace(/[^\d]/g, '');
  return cleanCPF.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * Apply CPF mask incrementally (000.000.000-00)
 * Used by frontend for real-time input masking
 * @param value Raw input value
 * @returns Masked CPF
 */
export function maskCPF(value: string): string {
  const cleanValue = value.replace(/[^\d]/g, '');
  let masked = cleanValue;
  
  if (cleanValue.length > 3) {
    masked = cleanValue.substring(0, 3) + '.' + cleanValue.substring(3);
  }
  if (cleanValue.length > 6) {
    masked = masked.substring(0, 7) + '.' + cleanValue.substring(6);
  }
  if (cleanValue.length > 9) {
    masked = masked.substring(0, 11) + '-' + cleanValue.substring(9, 11);
  }
  
  return masked.substring(0, 14);
}

/**
 * Remove tudo que não for [0-9A-Z] de um CNPJ e força uppercase.
 * Preparado para o CNPJ Alfanumérico (IN RFB 2.229/2024, vigência 07/2026):
 * as 12 primeiras posições podem conter letras A-Z além de dígitos,
 * enquanto as 2 últimas (DV) permanecem numéricas.
 */
export function onlyCnpjChars(value: string | null | undefined): string {
  return (value ?? '').toUpperCase().replace(/[^0-9A-Z]/g, '');
}

/**
 * Validate CNPJ (Brazilian Legal Entity Taxpayer Registry)
 *
 * Preparado para CNPJ Alfanumérico (IN RFB 2.229/2024, vigor 07/2026):
 *   - Posições 1-12 aceitam [0-9A-Z] (uppercase)
 *   - Posições 13-14 (DV) permanecem [0-9]
 *   - Cálculo do DV: valor de cada caractere = charCodeAt(0) - 48
 *     ('0'..'9' → 0..9; 'A'..'Z' → 17..42). Como '0'-'9' seguem o
 *     mesmo mapeamento, CNPJs 100% numéricos existentes continuam
 *     validando exatamente igual (backwards-compat).
 *   - Pesos e módulo 11 permanecem inalterados.
 * @param cnpj CNPJ string (can be with or without mask)
 * @returns true if CNPJ is valid, false otherwise
 */
export function validateCNPJ(cnpj: string): boolean {
  const clean = onlyCnpjChars(cnpj);

  if (clean.length !== 14) {
    return false;
  }

  if (/^([0-9A-Z])\1{13}$/.test(clean)) {
    return false;
  }

  // DV é sempre numérico, mesmo em CNPJs alfanuméricos.
  if (!/^[0-9]{2}$/.test(clean.substring(12))) {
    return false;
  }

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const valueAt = (i: number) => clean.charCodeAt(i) - 48;

  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += valueAt(i) * weights1[i];
  }
  let check = sum % 11;
  const digit1 = check < 2 ? 0 : 11 - check;
  if (digit1 !== valueAt(12)) {
    return false;
  }

  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += valueAt(i) * weights2[i];
  }
  check = sum % 11;
  const digit2 = check < 2 ? 0 : 11 - check;
  if (digit2 !== valueAt(13)) {
    return false;
  }

  return true;
}

/**
 * Format CNPJ with mask (00.000.000/0000-00)
 * Aceita entrada alfanumérica (CNPJ Alfanumérico); mantém o mesmo
 * layout de 18 caracteres com separadores, apenas com letras nas
 * posições 1-12 quando presentes.
 * @param cnpj CNPJ string (raw)
 * @returns Formatted CNPJ
 */
export function formatCNPJ(cnpj: string): string {
  const clean = onlyCnpjChars(cnpj);
  return clean.replace(/([0-9A-Z]{2})([0-9A-Z]{3})([0-9A-Z]{3})([0-9A-Z]{4})([0-9]{2})/, '$1.$2.$3/$4-$5');
}

/**
 * Validate Brazilian phone number (11 digits: DDD + 9 + 8 digits)
 * Format: (00) 9 0000-0000
 * @param phone Phone string (can be with or without mask)
 * @returns true if phone is valid, false otherwise
 */
export function validatePhone(phone: string): boolean {
  // Remove mask characters
  const cleanPhone = phone.replace(/[^\d]/g, '');
  
  // Must have exactly 11 digits
  if (cleanPhone.length !== 11) {
    return false;
  }
  
  // DDD (area code) must start with valid digits (11-99)
  const ddd = parseInt(cleanPhone.substring(0, 2));
  if (ddd < 11 || ddd > 99) {
    return false;
  }
  
  // Mobile numbers must start with 9
  if (cleanPhone.charAt(2) !== '9') {
    return false;
  }
  
  return true;
}

/**
 * Format phone with mask ((00) 9 0000-0000)
 * Used by backend for final formatting
 * @param phone Phone string (raw digits)
 * @returns Formatted phone
 */
export function formatPhone(phone: string): string {
  const cleanPhone = phone.replace(/[^\d]/g, '');
  return cleanPhone.replace(/(\d{2})(\d{1})(\d{4})(\d{4})/, '($1) $2 $3-$4');
}

/**
 * Aplica máscara CNPJ progressiva (00.000.000/0000-00).
 * Aceita entrada alfanumérica (CNPJ Alfanumérico — IN RFB 2.229/2024):
 * posições 1-12 aceitam [0-9A-Z] (forçadas a uppercase), posições 13-14
 * (DV) aceitam apenas [0-9]. Caracteres inválidos na posição atual são
 * descartados durante a digitação.
 */
export function maskCNPJ(value: string): string {
  const upper = (value ?? '').toUpperCase();
  const alnum = upper.replace(/[^0-9A-Z]/g, '');
  const head = alnum.substring(0, 12);
  const tailDigits = alnum.substring(12).replace(/[^0-9]/g, '').substring(0, 2);
  const clean = head + tailDigits;

  let masked = clean;
  if (clean.length > 2) masked = clean.substring(0, 2) + '.' + clean.substring(2);
  if (clean.length > 5) masked = clean.substring(0, 2) + '.' + clean.substring(2, 5) + '.' + clean.substring(5);
  if (clean.length > 8) masked = clean.substring(0, 2) + '.' + clean.substring(2, 5) + '.' + clean.substring(5, 8) + '/' + clean.substring(8);
  if (clean.length > 12) masked = clean.substring(0, 2) + '.' + clean.substring(2, 5) + '.' + clean.substring(5, 8) + '/' + clean.substring(8, 12) + '-' + clean.substring(12, 14);
  return masked.substring(0, 18);
}

/**
 * Aplica máscara progressiva de CEP (00000-000). Só aceita dígitos —
 * caracteres não-numéricos são descartados. Máximo de 8 dígitos.
 */
export function maskCEP(value: string): string {
  const clean = (value ?? '').replace(/\D/g, '').substring(0, 8);
  if (clean.length <= 5) return clean;
  return `${clean.substring(0, 5)}-${clean.substring(5)}`;
}

/**
 * Valida CEP: exatamente 8 dígitos, com ou sem máscara.
 */
export function validateCEP(value: string): boolean {
  return (value ?? '').replace(/\D/g, '').length === 8;
}

/**
 * Apply BRL currency mask incrementally (1.234,56)
 * Digits are consumed from the right, so the last two are always the cents.
 * Used by frontend for real-time input masking
 * @param value Raw input value
 * @returns Masked value, or empty string when there is no digit
 */
export function maskCurrencyBRL(value: string): string {
  const clean = value.replace(/[^\d]/g, '').replace(/^0+(?=\d)/, '').substring(0, 15);
  if (!clean) return '';
  const cents = clean.padStart(3, '0');
  const integerPart = cents.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${integerPart},${cents.slice(-2)}`;
}

/**
 * Convert a BRL masked value (1.234,56) back to a number
 * @param value Masked value
 * @returns Numeric value, or NaN when there is no digit
 */
export function parseCurrencyBRL(value: string): number {
  const clean = value.replace(/[^\d]/g, '');
  if (!clean) return NaN;
  return Number(clean) / 100;
}

/**
 * Máscara progressiva de telefone com DDD, que se adapta ao tamanho:
 * - até 10 dígitos (fixo): (00) 0000-0000
 * - 11 dígitos (celular):  (00) 0 0000-0000
 * Caracteres não numéricos são descartados e o excedente de 11 dígitos
 * é ignorado. Usada nos campos de telefone em tempo real.
 */
export function maskPhone(value: string): string {
  const digits = (value ?? '').replace(/\D/g, '').substring(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;

  const ddd = digits.substring(0, 2);
  const number = digits.substring(2);
  if (digits.length === 11) {
    return `(${ddd}) ${number.substring(0, 1)} ${number.substring(1, 5)}-${number.substring(5)}`;
  }
  if (number.length <= 4) return `(${ddd}) ${number}`;
  return `(${ddd}) ${number.substring(0, 4)}-${number.substring(4)}`;
}

/**
 * List of known email providers
 * Can be extended or moved to database configuration
 */
export const KNOWN_EMAIL_PROVIDERS = [
  'gmail.com',
  'outlook.com',
  'hotmail.com',
  'yahoo.com',
  'yahoo.com.br',
  'icloud.com',
  'uol.com.br',
  'terra.com.br',
  'bol.com.br',
  'ig.com.br',
  'live.com',
  'msn.com',
  'globo.com',
  'oi.com.br',
];

/**
 * Validate email with enhanced rules:
 * - Must contain @
 * - Domain must be known provider OR corporate domain (from companies table)
 * - Must end with .com or .com.br
 * @param email Email string
 * @param corporateDomains Optional list of corporate domains from database
 * @returns true if email is valid, false otherwise
 */
export function validateEmail(email: string, corporateDomains: string[] = []): boolean {
  // Basic format check
  if (!email || !email.includes('@')) {
    return false;
  }
  
  // Extract domain
  const parts = email.split('@');
  if (parts.length !== 2) {
    return false;
  }
  
  const domain = parts[1].toLowerCase();
  
  // Check TLD (.com or .com.br)
  if (!domain.endsWith('.com') && !domain.endsWith('.com.br')) {
    return false;
  }
  
  // Check if domain is in known providers or corporate domains
  const allAllowedDomains = [...KNOWN_EMAIL_PROVIDERS, ...corporateDomains.map(d => d.toLowerCase())];
  
  const isAllowed = allAllowedDomains.some(allowedDomain => 
    domain === allowedDomain.toLowerCase()
  );
  
  return isAllowed;
}

/**
 * Validate full name (must have at least 2 words)
 * @param name Full name string
 * @returns true if name is valid, false otherwise
 */
export function validateFullName(name: string): boolean {
  if (!name || name.trim().length === 0) {
    return false;
  }
  
  const words = name.trim().split(/\s+/);
  return words.length >= 2 && words.every(word => word.length > 0);
}

/**
 * Validate RG (minimum 5 characters, allows numbers and X)
 * @param rg RG string
 * @returns true if RG is valid, false otherwise
 */
export function validateRG(rg: string): boolean {
  if (!rg || rg.trim().length < 5) {
    return false;
  }
  
  // Allow numbers and X character
  return /^[0-9X]+$/i.test(rg.replace(/[.\-\s]/g, ''));
}

/**
 * Extract domain from email address
 * @param email Email address
 * @returns Domain or null if invalid
 */
export function extractEmailDomain(email: string): string | null {
  if (!email || !email.includes('@')) {
    return null;
  }
  
  const parts = email.split('@');
  if (parts.length !== 2) {
    return null;
  }
  
  return parts[1].toLowerCase();
}
